import type {
  Attempt, AnswerRecord, AttemptReviewSummary, AuthorExam, BatchDto, BatchUpsertRequest,
  CreateEitherOrRequest, CreateExamRequest, CreateInstitutionRequest, CreateOrganizationRequest, UpdateOrganizationRequest, CreateQuestionRequest,
  CreateSectionRequest, CreateStaffRequest, CreateStaffRoleRequest, CreateStudentRequest, DepartmentDto, UpdateSectionRequest,
  AcademicYear, CreateAcademicYearRequest,
  BatchYear, CreateBatchYearRequest, SemesterDto, UpdateSemesterRequest,
  ClassSectionDetail, CreateClassSectionDetailRequest,
  BatchGrid, ClassSectionDto, ClassSectionGrid, ClassSectionUpsertRequest,
  ExamPaper, ExamPeriodDto, ExamPeriodUpsertRequest, ExamScheduleDto, ExamScheduleUpsertRequest, ExamSummary, GenerateLoginsResult, ImportResult, Institution, LoginResponse, Organization, PermissionContext,
  PermissionSet, ProfileDetails, ProgramDto, ProgramType, RecordInfo, ReviewBundle,
  Rubric, Staff, StaffLogin, StaffRole, Student, StudentLogin, UpdateExamRequest, UpdateQuestionRequest, UpsertAnswerResponse,
  UpsertRubricRequest, ExamPattern, UpsertExamPatternRequest, Syllabus, GeneratedPaper, QuestionScoreInput,
  QuestionReviewInput
} from '../types/models';

const TOKEN_KEY = 'examapp.token';
const USER_KEY = 'examapp.user';
const MUSTCHANGE_KEY = 'examapp.mustchange';
const PERM_KEY = 'examapp.permissions';

// Use sessionStorage (per-tab) rather than localStorage (shared across all tabs
// of the origin). This lets each tab hold a separate login while still
// surviving a page refresh within the same tab.
const store = sessionStorage;

export const auth = {
  getToken: () => store.getItem(TOKEN_KEY),
  setToken: (t: string | null) => {
    if (t) store.setItem(TOKEN_KEY, t);
    else store.removeItem(TOKEN_KEY);
  },
  getUser: () => {
    const raw = store.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  },
  setUser: (u: unknown | null) => {
    if (u) store.setItem(USER_KEY, JSON.stringify(u));
    else store.removeItem(USER_KEY);
  },
  getMustChangePassword: () => store.getItem(MUSTCHANGE_KEY) === '1',
  setMustChangePassword: (v: boolean) => {
    if (v) store.setItem(MUSTCHANGE_KEY, '1');
    else store.removeItem(MUSTCHANGE_KEY);
  },
  // Permission context: { organizationId, organizationName, pages }. Admins are treated as
  // having every page regardless of what's stored.
  getPermissions: (): { organizationId: string | null; organizationName: string | null; pages: string[] } => {
    const raw = store.getItem(PERM_KEY);
    return raw ? JSON.parse(raw) : { organizationId: null, organizationName: null, pages: [] };
  },
  setPermissions: (p: unknown | null) => {
    if (p) store.setItem(PERM_KEY, JSON.stringify(p));
    else store.removeItem(PERM_KEY);
  },
  clear: () => {
    store.removeItem(TOKEN_KEY);
    store.removeItem(USER_KEY);
    store.removeItem(MUSTCHANGE_KEY);
    store.removeItem(PERM_KEY);
  }
};

const BASE = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

async function request<T>(method: string, path: string, body?: unknown, raw?: BodyInit, contentType?: string): Promise<T> {
  const headers: Record<string, string> = {};
  const token = auth.getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let payload: BodyInit | undefined;
  if (raw !== undefined) {
    payload = raw;
    if (contentType) headers['Content-Type'] = contentType;
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${BASE}${path}`, { method, headers, body: payload });
  if (res.status === 401) {
    auth.clear();
    if (!location.pathname.startsWith('/login')) location.href = '/login';
    throw new ApiError(401, 'Unauthorized');
  }
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = await res.json();
      msg = j.message || msg;
    } catch { /* body was not JSON */ }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') || '';
  return ct.includes('application/json') ? res.json() : (undefined as T);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

export const api = {
  login: (email: string, password: string) =>
    request<LoginResponse>('POST', '/auth/login', { email, password }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<boolean>('POST', '/auth/change-password', { currentPassword, newPassword }),

  // ----- My profile -----
  getMyProfile: () => request<ProfileDetails>('GET', '/profile/me'),
  uploadProfilePhoto: (data: Blob, contentType: string) =>
    request<ProfileDetails>('POST', '/profile/me/photo', undefined, data, contentType),

  // ----- Staff logins (admin) -----
  listStaffLogins: (organizationId: string, institutionId?: string, staffRoleId?: string, search?: string) => {
    const p = new URLSearchParams({ organizationId });
    if (institutionId) p.set('institutionId', institutionId);
    if (staffRoleId) p.set('staffRoleId', staffRoleId);
    if (search) p.set('search', search);
    return request<StaffLogin[]>('GET', `/staff-logins?${p.toString()}`);
  },
  generateLogins: (organizationId: string, staffRoleId: string, institutionId?: string) =>
    request<GenerateLoginsResult>('POST', '/staff-logins/generate',
      { organizationId, staffRoleId, institutionId: institutionId ?? null }),
  resetStaffLoginPassword: (loginId: string) =>
    request<boolean>('POST', `/staff-logins/${loginId}/reset-password`),
  setStaffLoginActive: (loginId: string, active: boolean) =>
    request<boolean>('PUT', `/staff-logins/${loginId}/active`, { active }),

  // ----- Students -----
  listStudents: (organizationId: string, batchId?: string, search?: string) => {
    const p = new URLSearchParams({ organizationId });
    if (batchId) p.set('batchId', batchId);
    if (search) p.set('search', search);
    return request<Student[]>('GET', `/students?${p.toString()}`);
  },
  createStudent: (req: CreateStudentRequest) =>
    request<Student>('POST', '/students', req),
  updateStudent: (id: string, req: Omit<CreateStudentRequest, 'organizationId'>) =>
    request<Student>('PUT', `/students/${id}`, req),
  deleteStudent: (id: string) =>
    request<boolean>('DELETE', `/students/${id}`),
  importStudents: (organizationId: string, rows: Record<string, string>[]) =>
    request<ImportResult>('POST', '/students/import', { organizationId, rows }),

  // ----- Student logins (admin) -----
  listStudentLogins: (organizationId: string, batchId?: string, search?: string) => {
    const p = new URLSearchParams({ organizationId });
    if (batchId) p.set('batchId', batchId);
    if (search) p.set('search', search);
    return request<StudentLogin[]>('GET', `/student-logins?${p.toString()}`);
  },
  generateStudentLogins: (organizationId: string, batchId: string) =>
    request<GenerateLoginsResult>('POST', '/student-logins/generate', { organizationId, batchId }),
  resetStudentLoginPassword: (loginId: string) =>
    request<boolean>('POST', `/student-logins/${loginId}/reset-password`),
  setStudentLoginActive: (loginId: string, active: boolean) =>
    request<boolean>('PUT', `/student-logins/${loginId}/active`, { active }),

  listExams: () => request<ExamSummary[]>('GET', '/exams'),
  getPaper: (examId: string) => request<ExamPaper>('GET', `/exams/${examId}/paper`),

  startAttempt: (examId: string) => request<Attempt>('POST', `/attempts/start/${examId}`),
  getMyAttempt: (examId: string) => request<Attempt>('GET', `/attempts/mine/${examId}`),
  getAnswers: (attemptId: string) => request<AnswerRecord[]>('GET', `/attempts/${attemptId}/answers`),
  upsertAnswer: (attemptId: string, questionId: string, contentJson: string, clientHash: string) =>
    request<UpsertAnswerResponse>('PUT', `/attempts/${attemptId}/answers`,
      { questionId, contentJson, clientHash }),
  submitAttempt: (attemptId: string, auto = false) =>
    request<Attempt>('POST', `/attempts/${attemptId}/submit${auto ? '?auto=true' : ''}`),
  heartbeat: (attemptId: string) => request<void>('POST', `/attempts/${attemptId}/heartbeat`),

  uploadBlob: (attemptId: string, questionId: string, data: Blob, contentType: string) =>
    request<{ blobKey: string; presignedUrl: string }>(
      'POST', `/attempts/${attemptId}/questions/${questionId}/blob`,
      undefined, data, contentType),

  listExamAttempts: (examId: string) =>
    request<AttemptReviewSummary[]>('GET', `/exams/${examId}/attempts`),

  getReviewBundle: (attemptId: string) =>
    request<ReviewBundle>('GET', `/attempts/${attemptId}/review`),

  gradeAttempt: (attemptId: string, questionScores: QuestionScoreInput[], notes: string | null,
                 reviews: QuestionReviewInput[] = []) =>
    request<AttemptReviewSummary>('PUT', `/attempts/${attemptId}/grade`,
      { questionScores, notes, reviews }),

  // Student's own published result + re-evaluation request.
  getMyResult: (examId: string) => request<ReviewBundle>('GET', `/attempts/mine/${examId}/result`),
  requestReevaluation: (examId: string, reason: string) =>
    request<boolean>('POST', `/attempts/mine/${examId}/reevaluation`, { reason }),
  resolveReevaluation: (attemptId: string) =>
    request<boolean>('POST', `/attempts/${attemptId}/reevaluation/resolve`),

  devEnabled: () => request<{ enabled: boolean }>('GET', '/dev/enabled'),

  resetMyAttempt: (examId: string) =>
    request<boolean>('POST', `/dev/attempts/reset/${examId}`),

  // ----- Organizations / Institutions (admin) -----
  listOrganizations: () => request<Organization[]>('GET', '/organizations'),
  createOrganization: (req: CreateOrganizationRequest) =>
    request<Organization>('POST', '/organizations', req),
  updateOrganization: (id: string, req: UpdateOrganizationRequest) =>
    request<Organization>('PUT', `/organizations/${id}`, req),

  listInstitutions: (organizationId?: string) =>
    request<Institution[]>('GET',
      organizationId ? `/institutions?organizationId=${organizationId}` : '/institutions'),
  createInstitution: (req: CreateInstitutionRequest) =>
    request<Institution>('POST', '/institutions', req),
  updateInstitution: (id: string, code: string, name: string) =>
    request<Institution>('PUT', `/institutions/${id}`, { code, name }),
  deleteInstitution: (id: string) =>
    request<boolean>('DELETE', `/institutions/${id}`),

  // ----- Authoring -----
  listDepartments: (organizationId?: string) =>
    request<DepartmentDto[]>('GET',
      organizationId ? `/departments?organizationId=${organizationId}` : '/departments'),
  createDepartment: (institutionId: string, code: string, name: string) =>
    request<DepartmentDto>('POST', '/departments', { institutionId, code, name }),
  updateDepartment: (id: string, code: string, name: string) =>
    request<DepartmentDto>('PUT', `/departments/${id}`, { code, name }),
  deleteDepartment: (id: string) =>
    request<boolean>('DELETE', `/departments/${id}`),

  // ----- Academic years (org-level master) -----
  listAcademicYears: (organizationId: string, search?: string) => {
    const p = new URLSearchParams({ organizationId });
    if (search) p.set('search', search);
    return request<AcademicYear[]>('GET', `/academic-years?${p.toString()}`);
  },
  createAcademicYear: (req: CreateAcademicYearRequest) =>
    request<AcademicYear>('POST', '/academic-years', req),
  updateAcademicYear: (id: string, req: Omit<CreateAcademicYearRequest, 'organizationId'>) =>
    request<AcademicYear>('PUT', `/academic-years/${id}`, req),
  deleteAcademicYear: (id: string) =>
    request<boolean>('DELETE', `/academic-years/${id}`),

  // ----- Batch years (org-level master; drives a batch's start/end dates) -----
  listBatchYears: (organizationId: string, search?: string) => {
    const p = new URLSearchParams({ organizationId });
    if (search) p.set('search', search);
    return request<BatchYear[]>('GET', `/batch-years?${p.toString()}`);
  },
  createBatchYear: (req: CreateBatchYearRequest) =>
    request<BatchYear>('POST', '/batch-years', req),
  updateBatchYear: (id: string, req: Omit<CreateBatchYearRequest, 'organizationId'>) =>
    request<BatchYear>('PUT', `/batch-years/${id}`, req),
  deleteBatchYear: (id: string) =>
    request<boolean>('DELETE', `/batch-years/${id}`),

  // ----- Semesters (auto-generated per batch year) -----
  listSemesters: (batchYearId: string) =>
    request<SemesterDto[]>('GET', `/semesters?batchYearId=${batchYearId}`),
  updateSemester: (id: string, req: UpdateSemesterRequest) =>
    request<SemesterDto>('PUT', `/semesters/${id}`, req),
  regenerateSemesters: (batchYearId: string) =>
    request<SemesterDto[]>('POST', `/semesters/regenerate?batchYearId=${batchYearId}`),

  // ----- Programs (live under a department; batches live under a program) -----
  listPrograms: (organizationId?: string) =>
    request<ProgramDto[]>('GET',
      organizationId ? `/programs?organizationId=${organizationId}` : '/programs'),
  createProgram: (departmentId: string, code: string, name: string, type: ProgramType | null) =>
    request<ProgramDto>('POST', '/programs', { departmentId, code, name, type }),
  updateProgram: (id: string, code: string, name: string, type: ProgramType | null) =>
    request<ProgramDto>('PUT', `/programs/${id}`, { code, name, type }),
  deleteProgram: (id: string) =>
    request<boolean>('DELETE', `/programs/${id}`),

  // ----- Permissions -----
  getMyPermissions: () => request<PermissionContext>('GET', '/permissions/me'),
  getStaffPermissions: (organizationId: string, staffRoleId: string) =>
    request<PermissionSet>('GET', `/permissions/staff?organizationId=${organizationId}&staffRoleId=${staffRoleId}`),
  setStaffPermissions: (organizationId: string, staffRoleId: string, pages: string[]) =>
    request<PermissionSet>('PUT', '/permissions/staff', { organizationId, staffRoleId, pages }),
  getStudentPermissions: (organizationId: string) =>
    request<PermissionSet>('GET', `/permissions/student?organizationId=${organizationId}`),
  setStudentPermissions: (organizationId: string, pages: string[]) =>
    request<PermissionSet>('PUT', '/permissions/student', { organizationId, pages }),

  // ----- Staff roles -----
  listStaffRoles: (organizationId?: string) =>
    request<StaffRole[]>('GET',
      organizationId ? `/staff-roles?organizationId=${organizationId}` : '/staff-roles'),
  createStaffRole: (req: CreateStaffRoleRequest) =>
    request<StaffRole>('POST', '/staff-roles', req),
  updateStaffRole: (id: string, code: string, name: string) =>
    request<StaffRole>('PUT', `/staff-roles/${id}`, { code, name }),
  deleteStaffRole: (id: string) =>
    request<boolean>('DELETE', `/staff-roles/${id}`),

  // ----- Staff -----
  listStaff: (organizationId?: string) =>
    request<Staff[]>('GET',
      organizationId ? `/staff?organizationId=${organizationId}` : '/staff'),
  createStaff: (req: CreateStaffRequest) =>
    request<Staff>('POST', '/staff', req),
  updateStaff: (id: string, req: Omit<CreateStaffRequest, 'organizationId'>) =>
    request<Staff>('PUT', `/staff/${id}`, req),
  deleteStaff: (id: string) =>
    request<boolean>('DELETE', `/staff/${id}`),
  importStaff: (organizationId: string, rows: Record<string, string>[]) =>
    request<ImportResult>('POST', '/staff/import', { organizationId, rows }),
  listBatches: (programId: string) =>
    request<BatchDto[]>('GET', `/programs/${programId}/batches`),
  listAllBatches: (organizationId: string) =>
    request<BatchGrid[]>('GET', `/batches?organizationId=${organizationId}`),
  createBatch: (programId: string, req: BatchUpsertRequest) =>
    request<BatchDto>('POST', `/programs/${programId}/batches`, req),
  updateBatch: (programId: string, batchId: string, req: BatchUpsertRequest) =>
    request<BatchDto>('PUT', `/programs/${programId}/batches/${batchId}`, req),
  deleteBatch: (programId: string, batchId: string) =>
    request<boolean>('DELETE', `/programs/${programId}/batches/${batchId}`),

  // ----- Class sections (live under a program) -----
  listClassSections: (programId: string) =>
    request<ClassSectionDto[]>('GET', `/programs/${programId}/class-sections`),
  listAllClassSections: (organizationId: string) =>
    request<ClassSectionGrid[]>('GET', `/class-sections?organizationId=${organizationId}`),
  createClassSection: (programId: string, req: ClassSectionUpsertRequest) =>
    request<ClassSectionDto>('POST', `/programs/${programId}/class-sections`, req),
  updateClassSection: (programId: string, id: string, req: ClassSectionUpsertRequest) =>
    request<ClassSectionDto>('PUT', `/programs/${programId}/class-sections/${id}`, req),
  deleteClassSection: (programId: string, id: string) =>
    request<boolean>('DELETE', `/programs/${programId}/class-sections/${id}`),

  // ----- Class section details (Department → Program → Academic Year → Class Section) -----
  listClassSectionDetails: (organizationId: string, academicYearId?: string, search?: string) => {
    const p = new URLSearchParams({ organizationId });
    if (academicYearId) p.set('academicYearId', academicYearId);
    if (search) p.set('search', search);
    return request<ClassSectionDetail[]>('GET', `/class-section-details?${p.toString()}`);
  },
  createClassSectionDetail: (req: CreateClassSectionDetailRequest) =>
    request<ClassSectionDetail>('POST', '/class-section-details', req),
  updateClassSectionDetail: (id: string, req: Omit<CreateClassSectionDetailRequest, 'organizationId'>) =>
    request<ClassSectionDetail>('PUT', `/class-section-details/${id}`, req),
  deleteClassSectionDetail: (id: string) =>
    request<boolean>('DELETE', `/class-section-details/${id}`),

  uploadPromptImage: (data: Blob, contentType: string) =>
    request<{ url: string }>('POST', '/exams/prompt-images', undefined, data, contentType),

  createExam: (req: CreateExamRequest) => request<string>('POST', '/exams', req),
  updateExam: (examId: string, req: UpdateExamRequest) => request<boolean>('PUT', `/exams/${examId}`, req),
  deleteExam: (examId: string) => request<boolean>('DELETE', `/exams/${examId}`),
  getAuthorExam: (examId: string) => request<AuthorExam>('GET', `/exams/${examId}/author`),
  addSection: (examId: string, req: CreateSectionRequest) =>
    request<string>('POST', `/exams/${examId}/sections`, req),
  updateSection: (examId: string, sectionId: string, req: UpdateSectionRequest) =>
    request<boolean>('PUT', `/exams/${examId}/sections/${sectionId}`, req),
  deleteSection: (examId: string, sectionId: string) =>
    request<boolean>('DELETE', `/exams/${examId}/sections/${sectionId}`),
  addQuestion: (examId: string, req: CreateQuestionRequest) =>
    request<string>('POST', `/exams/${examId}/questions`, req),
  addEitherOrQuestion: (examId: string, req: CreateEitherOrRequest) =>
    request<string[]>('POST', `/exams/${examId}/questions/either-or`, req),
  updateQuestion: (examId: string, questionId: string, req: UpdateQuestionRequest) =>
    request<boolean>('PUT', `/exams/${examId}/questions/${questionId}`, req),
  deleteQuestion: (examId: string, questionId: string) =>
    request<boolean>('DELETE', `/exams/${examId}/questions/${questionId}`),
  upsertRubric: (examId: string, questionId: string, req: UpsertRubricRequest) =>
    request<Rubric>('PUT', `/exams/${examId}/questions/${questionId}/rubric`, req),
  deleteRubric: (examId: string, questionId: string) =>
    request<boolean>('DELETE', `/exams/${examId}/questions/${questionId}/rubric`),
  getRecordInfo: (entity: string, id: string) => request<RecordInfo>('GET', `/audit/${entity}/${id}`),

  // ----- Exam schedule (timetable) -----
  // ----- Exam periods (parent of the timetable; subjects live inside a period) -----
  listExamPeriods: (organizationId: string) =>
    request<ExamPeriodDto[]>('GET', `/exam-periods?organizationId=${organizationId}`),
  getExamPeriod: (id: string) =>
    request<ExamPeriodDto>('GET', `/exam-periods/${id}`),
  createExamPeriod: (req: ExamPeriodUpsertRequest) =>
    request<ExamPeriodDto>('POST', '/exam-periods', req),
  updateExamPeriod: (id: string, req: ExamPeriodUpsertRequest) =>
    request<ExamPeriodDto>('PUT', `/exam-periods/${id}`, req),
  deleteExamPeriod: (id: string) =>
    request<boolean>('DELETE', `/exam-periods/${id}`),

  listExamSchedules: (organizationId: string, examPeriodId?: string) => {
    const p = new URLSearchParams({ organizationId });
    if (examPeriodId) p.set('examPeriodId', examPeriodId);
    return request<ExamScheduleDto[]>('GET', `/exam-schedules?${p.toString()}`);
  },
  listMyExamSchedules: () =>
    request<ExamScheduleDto[]>('GET', '/exam-schedules/mine'),
  createExamSchedule: (req: ExamScheduleUpsertRequest) =>
    request<ExamScheduleDto>('POST', '/exam-schedules', req),
  updateExamSchedule: (id: string, req: ExamScheduleUpsertRequest) =>
    request<ExamScheduleDto>('PUT', `/exam-schedules/${id}`, req),
  publishExamSchedule: (id: string, published: boolean) =>
    request<ExamScheduleDto>('POST', `/exam-schedules/${id}/publish`, { published }),
  deleteExamSchedule: (id: string) =>
    request<boolean>('DELETE', `/exam-schedules/${id}`),
  importExamSchedules: (organizationId: string, rows: Record<string, string>[]) =>
    request<ImportResult>('POST', '/exam-schedules/import', { organizationId, rows }),

  publishExam: (examId: string) => request<ExamSummary>('POST', `/exams/${examId}/publish`),
  publishResults: (examId: string, publishAt: string) =>
    request<ExamSummary>('POST', `/exams/${examId}/publish-results`, { publishAt }),

  // ----- Exam patterns (per-department blueprint) -----
  listExamPatterns: (departmentId: string) =>
    request<ExamPattern[]>('GET', `/exam-patterns?departmentId=${departmentId}`),
  createExamPattern: (req: UpsertExamPatternRequest) =>
    request<ExamPattern>('POST', '/exam-patterns', req),
  updateExamPattern: (id: string, req: UpsertExamPatternRequest) =>
    request<ExamPattern>('PUT', `/exam-patterns/${id}`, req),
  deleteExamPattern: (id: string) =>
    request<boolean>('DELETE', `/exam-patterns/${id}`),

  // ----- Syllabi + AI question-paper generation -----
  listSyllabi: (departmentId: string) =>
    request<Syllabus[]>('GET', `/syllabi?departmentId=${departmentId}`),
  uploadSyllabus: (departmentId: string, title: string, courseCode: string, semester: number,
                   textbook: string, fileName: string, data: Blob) => {
    const p = new URLSearchParams({ departmentId, title, courseCode, semester: String(semester), textbook, fileName });
    return request<{ id: string }>('POST', `/syllabi?${p.toString()}`, undefined, data, 'application/pdf');
  },
  deleteSyllabus: (id: string) =>
    request<boolean>('DELETE', `/syllabi/${id}`),
  generatePaper: (syllabusId: string, examPatternId: string, units: string) =>
    request<GeneratedPaper>('POST', '/syllabi/generate', { syllabusId, examPatternId, units }),
  acceptGeneratedPaper: (syllabusId: string, paper: GeneratedPaper) =>
    request<string>('POST', '/syllabi/accept', { syllabusId, paper })
};
